const sectionNamesOrdered = [
  'intro',
  'core-capabilities',
  'asking-questions-and-getting-answers',
  'customization-and-modularity',
  'transparency-and-source-verification',
  'pricing-access-and-onboarding',
  'security-and-privacy',
];

const visibleDigitalAnalystURL = 'ai.tornado.com';
const digitalAnalystURL = 'https://ai.tornado.com';
const supportEmail = ' <a href="mailto:ai@tornado.com">ai@tornado.com</a>';
const linkToDigitalAnalyst = `<a href="${digitalAnalystURL}" target="_blank">${visibleDigitalAnalystURL}</a>`;
const linkToBookADemo = 'https://calendar.app.google/R8W5AX9C3TTrFg2L9';

// const nextFAQId = 1;
/*
        {
          id: 1,
          question: '',
          message: '',
        },
*/

const FAQData = () => {
  const sectionLookup = {
    intro: {
      name: 'intro',
      heading: "Introduction to Tornado's Digital Analyst",
      questions: [
        {
          id: 1,
          question: 'About Tornado',
          message: `<p>Tornado revolutionizes financial research by delivering end-to-end automated workflows powered by human-level AI analysis. Unlike other AI tools that only help with one step in your process, the Digital Analyst orchestrates and accelerates every stage, from ingesting data, to performing bespoke firm-specific analysis, to delivering actionable insights and pre-formatted Word, Excel, and Powerpoint end products.<p/>
          <p>Purpose-built by experienced analysts, PMs, quants and engineers, the Digital Analyst integrates seamlessly into your current workflow, delivering consistent, reliable, human-quality work in a fraction of the time at a fraction of the cost. With virtually unlimited scaling, Tornado empowers teams to find opportunities faster, make better decisions, and stay focused on what they do best, while reducing or eliminating grunt work.<p/>
          <p>You can sign up for the Digital Analyst at ${linkToDigitalAnalyst}</p>`,
        },
        {
          id: 2,
          question: 'What is Tornado’s Digital Analyst?',
          message: `<p>Tornado's Digital Analyst is an AI-powered platform purpose-built for financial research. It synthesizes data from millions of public sources, along with your firm’s proprietary data, enabling end-to-end human-quality automated workflows at enterprise scale.<p/>
          <p>The platform is designed to:</p>
          <ul>
          <li>Help identify, evaluate, and track companies of interest</li>
          <li>Leverage Tornado’s up-to-date database of public filings alongside your internal research and document library</li>
          <li>Enable extensive integration with document repositories and data feeds</li>
          <li>Ensure accurate attribution, with transparent source information and optional in-line citations</li>
          <li>Generate reports, updates, emails, investor letters and much more on demand</li>
          <li>Analyze sentiment in Fed releases, pressers, and speeches in seconds</li>
          <li>Deliver agentic orchestration to consistently produce human quality work</li>
          </ul>
          <p>
          Sign up with your work email at ${linkToDigitalAnalyst} to start a trial.
          <p/>
          <img src="/images/tornado-ai-co-pilot.svg" alt="Digital Analyst Workflow" style="width: 100%; margin-top: 16px;" />
          `,
        },
        {
          id: 3,
          question: 'How is the Digital Analyst different from other AI platforms? ',
          message:
            '<p>Unlike general AI tools and LLMs, Tornado’s Digital Analyst is purpose-built for financial professionals:</p><ul><li>Pre-loaded with millions of pages of company filings, earnings calls, Fed releases, etc. updated multiple times per hour</li><li>Able to do complex math, ranking/scoring, find exact quotes, and other critical financial tasks where general LLMs fall short</li><li>Understands logic in financial models and other spreadsheets, and answers natural language queries about them</li><li>Precise source attribution available for every response</li><li>Proprietary client data is kept in a ringfenced VPC, encrypted with finance grade TLS 1.2 in transit and AES-256 at rest, with no possibility of leakage to other clients or the public internet</li><li>We never train or fine-tune LLMs or other AI models on client data</li><li>Integrates directly with your firm’s data sources and storage</li></ul>',
        },
      ],
    },
    'core-capabilities': {
      name: 'core-capabilities',
      heading: 'Core Capabilities',
      questions: [
        {
          id: 4,
          question: 'How does the Digital Analyst work?',
          message:
            '<p>The Digital Analyst orchestrates multiple cutting-edge analytical processes, including generative artificial intelligence models and deterministic algorithms, to analyze millions of pages of financial data from over 4,500 public companies in the US and Canada, plus your firm’s own sources. The platform continuously updates throughout the day and provides a suite of powerful tools for comprehensive and highly customizable analysis, transforming raw data to insight in seconds.</p><p>The Digital Analyst ingests your proprietary data, whether structured or unstructured, in a wide range of formats including PDF, Word, PowerPoint, Excel, txt, html, relational databases and more.  Documents can be uploaded manually or automatically from shared drives, email, CRMs and other sources.</p><p>The Digital Analyst is not one-size-fits all. The platform is highly customizable with true end-to-end workflows that empower your team to uncover deeper insights and make better decisions, faster than ever.</p>',
        },
      ],
    },
    'asking-questions-and-getting-answers': {
      name: 'asking-questions-and-getting-answers',
      heading: 'Asking Questions & Getting Answers',
      questions: [
        {
          id: 5,
          question: 'Can I choose how an answer to a query is formatted?',
          message:
            "Yes! You can specify how you'd like the information presented. When entering a question, ask for the output format that suits your needs, such as:<ul><li>Summary</li><li>Bulleted list</li><li>Comparison chart or table</li><li>Key points grouped by topic</li><li>Bold certain numbers or words</li></ul>For example, you could ask: “Summarize the risks in bullet points” or “List the top 5 growth drivers in a table”",
        },
        {
          id: 6,
          question: 'Can I ask follow-up questions or refine my search?',
          message:
            'Absolutely. With a single click, you can dive deeper into any answer or search for more detail. Tornado also provides three auto-generated follow-up questions with each response. These are designed to help you explore areas where the Digital Analyst has identified high data density—making it easier to uncover insights you might not have thought to ask about directly.',
        },
      ],
    },
    'customization-and-modularity': {
      name: 'customization-and-modularity',
      heading: 'Customization & Modularity',
      questions: [
        {
          id: 7,
          question: "Can the Digital Analyst be customized to fit my organization's specific needs? ",
          message:
            'Yes.  We understand that every firm has unique workflows, and the Digital Analyst is designed with that in mind.  Our AI engineers and product specialists will work with you to set up custom research modes, build output templates that match your firm’s style and conventions, automate ingestion of key data sources, and much more.  Whitelabeling of the platform to match your firm’s logo and color branding is also available.',
        },
      ],
    },
    'transparency-and-source-verification': {
      name: 'transparency-and-source-verification',
      heading: 'Transparency & Source Verification',
      questions: [
        {
          id: 8,
          question: 'How does Tornado provide high-quality and accurate financial data?',
          message:
            '<p>The Digital Analyst sources its data from an up-to-date repository of regulatory filings and earnings calls from over 4,500 public companies, including the full Russell 3000, in the United States and Canada, as well as other reliable sources.</p><p>You may upload additional data or a tailored analysis experience. Our engineering team can automate these processes for you as well.</p>',
        },
        {
          id: 9,
          question: 'Are you able to see the source information for each answer? ',
          message:
            '<p>Absolutely. Tornado’s Digital Analyst is built for accuracy and auditability.</p><ul><li>Every answer is fully cited in the ‘Sources’ section, and you can click ‘Add Citation’ to view sources in the original document at the sentence level</li><li>Access to the full original documents, including SEC filings, earnings transcripts, Fed releases and more, is always available with a single click</li><li>Advanced orchestration and automated refinement and checks to virtually eliminate hallucinations</li></ul>',
        },
      ],
    },
    'pricing-access-and-onboarding': {
      name: 'pricing-access-and-onboarding',
      heading: 'Pricing, Access, and Onboarding',
      questions: [
        {
          id: 10,
          question: 'How much does the Digital Analyst cost? ',
          message: `<p>Pricing is customized for each partner based on:</p><ul><li>Team size</li><li>Data intensity</li><li>Extent of customization</li></ul><p>Tornado's Digital Analyst offers pricing to accommodate the diverse needs of our various clients. Our competitive pricing reflects the value and efficiency our tool brings to your research and analysis processes, augmenting your team with human-level work at a fraction of the cost of an additional employee.</p><p>To receive a tailored quote or schedule a demo, email us at <a href="mailto:ai@tornado.com">ai@tornado.com</a>.</p>`,
        },
        {
          id: 11,
          question: 'Can I pay for Tornado with soft dollars?',
          message:
            'Several clients are currently paying for Tornado with prime broker soft dollars. We are happy to work with you and your prime broker to enable this payment option.',
        },
        {
          id: 12,
          question: 'How do I start a trial? ',
          message: `<p>Tornado offers a trial so you can evaluate the platform with no commitment.</p><ul><li><b>Individual Trial</b> - Sign up instantly with your work email at ${linkToDigitalAnalyst}</li><li><b>Team Trial</b> - For multi-user trials, please email ${supportEmail} and we’ll make sure your team is set up with shared access and support</li></ul>`,
        },
        {
          id: 13,
          question: 'What is included in the trial?',
          message:
            'Your trial includes full access to the platform, a live demo with a senior Tornado team member, and a private, secure workspace for uploads. For longer trials or trials including custom analysis modes and automated data ingestion, discounted paid engagements are available. Please email us at <a href="mailto:ai@tornado.com">ai@tornado.com</a> for additional information.',
        },
        {
          id: 14,
          question: 'How can I set up a demo of the Digital Analyst? ',
          message: `<p>To see Tornado’s Digital Analyst in action, schedule a live demo with our team.</p><ul><li><b>Email</b> - ${supportEmail}</li><li><b>Schedule a call</b> - <a href="${linkToBookADemo}" target="_blank">Book a time here</a></li></ul><p>During the session, we’ll walk through your research needs and show you how Tornado can save you time, improve consistency, and elevate your team's investment decision-making.</p>`,
        },
      ],
    },
    'security-and-privacy': {
      name: 'security-and-privacy',
      heading: 'Security & Privacy',
      questions: [
        {
          id: 15,
          question: "Is my data and search activity private when using Tornado's Digital Analyst?",
          message:
            'Yes, your data and search activity are securely ring-fenced when using the Digital Analyst. We prioritize the confidentiality of your research and ensure that your data and results are not shared or used to train an LLM or other AI model. This guarantees that the insights you gain remain exclusive to you and your organization.',
        },
        {
          id: 16,
          question: 'How does Tornado ensure the security and privacy of my data? ',
          message: `<p>Your data is yours. Period.</p><ul><li>Proprietary uploads are never shared or used for model training</li><li>Client workspaces are ring-fenced and encrypted in a protected VPC on AWS</li><li>DDQs and security documentation available upon request</li></ul><p>Tornado prioritizes the security and privacy of your data. Our platform employs advanced encryption and security protocols to protect your information. All searches and data are stored privately, protecting your proprietary research keeping your data confidential and secure. We are happy to partner with your compliance or IT team for additional diligence, contact us at ${supportEmail}.</p>`,
        },
      ],
    },
  };

  const orderedSections = sectionNamesOrdered.map((n) => {
    return sectionLookup[n];
  });

  return {
    orderedSections,
    sectionNamesOrdered,
    sectionLookup,
  };
};

export default FAQData;
